import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Textarea } from "design-system/atoms/Textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { VaultVariableAutocomplete } from "modules/shared/components/VaultVariableAutocomplete"
import { cn } from "design-system/utils"
import { Minus, Plus, Trash2 } from "lucide-react"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { JsonSchemaTypeSchema } from "schema"
import z from "zod"

type VariableOption = {
  value: string
  label: string
}

type FieldType = z.infer<typeof JsonSchemaTypeSchema>

type Field = {
  name: string
  type: FieldType
  description?: string // For schema mode - description of what the field means
  required?: boolean // For schema mode - whether the field is required
  value?: string // For key-value mode (headers/body)
}

type Props = {
  label?: string
  description?: string
  action?: React.ReactNode
  value: Field[] | undefined
  onChange: (fields: Field[]) => void
  required?: boolean
  minFields?: number
  error?: string | null
  mode?: "schema" | "keyValue" // schema = name + type, keyValue = name + value
  showType?: boolean // only in keyValue mode: adds a string/number/boolean type selector per field
  variables?: VariableOption[]
}

const FIELD_TYPES: { value: "string" | "number" | "boolean" | "enum"; label: string }[] = [
  { value: "string", label: "String" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "enum", label: "Enum" },
]

function getFieldsFromValue(
  value: Field[] | undefined,
  required: boolean,
  minFields: number,
  mode: "schema" | "keyValue",
): Field[] {
  if (value && value.length > 0) {
    return value
  }

  if (required || minFields > 0) {
    return [
      {
        name: "",
        type: "string",
        description: mode === "schema" ? "" : undefined,
        required: mode === "schema" ? false : undefined,
        value: mode === "keyValue" ? "" : undefined,
      },
    ]
  }

  return []
}

export function FieldsForm({
  label,
  description,
  action,
  value,
  onChange,
  required = false,
  minFields = 0,
  error,
  mode = "schema",
  showType = false,
  variables = [],
}: Props) {
  const { t } = useTranslation()

  const fields = useMemo(
    () => getFieldsFromValue(value, required, minFields, mode),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(value), required, minFields, mode],
  )

  const handleFieldChange = useCallback(
    (index: number, updates: Partial<Field>) => {
      const updatedFields = fields.map((field, i) =>
        i === index ? { ...field, ...updates } : field,
      )
      onChange(updatedFields)
    },
    [fields, onChange],
  )

  const handleFieldTypeChange = useCallback(
    (index: number, value: string) => {
      let newType: FieldType

      switch (value) {
        case "enum":
          newType = { kind: value, values: [""] }
          break
        case "string":
        case "number":
        case "boolean":
          newType = value
          break
        default:
          throw new Error(`Cannot assign value of kind ${value}`)
      }

      handleFieldChange(index, { type: newType })
    },
    [handleFieldChange],
  )

  const handleEnumValuesChange = useCallback(
    (index: number, values: string[]) => {
      const field = fields.at(index)
      if (!field || typeof field.type !== "object" || field.type.kind !== "enum") {
        throw new Error("Type is not an enum!!1")
      }

      handleFieldChange(index, { type: { kind: "enum", values } })
    },
    [fields, handleFieldChange],
  )

  const handleRemoveField = useCallback(
    (index: number) => {
      if (fields.length <= minFields) return
      const updatedFields = fields.filter((_, i) => i !== index)
      onChange(updatedFields)
    },
    [fields, minFields, onChange],
  )

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex lg:flex-row flex-col items-start gap-3">
        <div className="flex flex-1 flex-col gap-1.5">
          {label && <Label>{label}</Label>}
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
        {action && action}
      </div>
      <div className="flex flex-col gap-2.5">
        {fields.map((field, index) => {
          const resolvedFieldValue = typeof field.type === "object" ? field.type.kind : field.type

          return (
            <div key={index} className="flex flex-col gap-2.5 border rounded-md p-3 bg-muted">
              {mode === "keyValue" ? (
                <div
                  className={cn(
                    "grid items-center gap-2.5",
                    showType
                      ? "grid-cols-[minmax(0,1fr)_7rem_minmax(0,2fr)_2.25rem]"
                      : "grid-cols-[minmax(0,1fr)_minmax(0,2fr)_2.25rem]",
                  )}
                >
                  <Input
                    placeholder={t("capabilities.webhookConfig.fieldsForm.fieldNamePlaceholder")}
                    value={field.name}
                    onChange={(e) => handleFieldChange(index, { name: e.target.value })}
                    className="min-w-0"
                  />
                  {showType && (
                    <Select
                      value={resolvedFieldValue ?? "string"}
                      onValueChange={(value) => handleFieldTypeChange(index, value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.filter((type) => type.value !== "enum").map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <VaultVariableAutocomplete variables={variables} className="min-w-0">
                    <Input
                      placeholder={t("capabilities.webhookConfig.fieldsForm.fieldValuePlaceholder")}
                      value={field.value ?? ""}
                      onChange={(e) => handleFieldChange(index, { value: e.target.value })}
                      className="w-full min-w-0"
                    />
                  </VaultVariableAutocomplete>
                  <div className="flex justify-end">
                    {fields.length > minFields && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveField(index)}
                        aria-label={t("capabilities.webhookConfig.fieldsForm.removeField")}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-1 flex-row items-center gap-2.5">
                  <Input
                    placeholder={t("capabilities.webhookConfig.fieldsForm.fieldNamePlaceholder")}
                    value={field.name}
                    onChange={(e) => handleFieldChange(index, { name: e.target.value })}
                    className="flex-1"
                  />
                  <div className="flex flex-row items-center gap-3">
                    <Select
                      value={resolvedFieldValue}
                      onValueChange={(value) => handleFieldTypeChange(index, value)}
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {mode === "schema" &&
                typeof field.type === "object" &&
                field.type.kind === "enum" && (
                  <div className="flex gap-2 flex-wrap items-center">
                    {field.type.values.map((value, valueIndex) => (
                      <div className="relative" key={valueIndex}>
                        <Input
                          className="pr-6 w-36"
                          value={value}
                          onChange={(event) => {
                            const newValues = (field.type as { values: string[] }).values.map(
                              (val, vIndex) => (vIndex === valueIndex ? event.target.value : val),
                            )
                            handleEnumValuesChange(index, newValues)
                          }}
                          placeholder={t("capabilities.config.enumValue")}
                        />
                        {valueIndex !== 0 && (
                          <Button
                            type="button"
                            className="absolute right-1 top-1 size-8"
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              const newValues = (field.type as { values: string[] }).values.filter(
                                (_, vIndex) => vIndex !== valueIndex,
                              )
                              handleEnumValuesChange(index, newValues)
                            }}
                          >
                            <span className="sr-only">{t("shared.delete")}</span>
                            <Minus />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        handleEnumValuesChange(index, [
                          ...(field.type as { values: string[] }).values,
                          "",
                        ])
                      }
                    >
                      <span>{t("shared.add")}</span>
                      <Plus />
                    </Button>
                  </div>
                )}
              {mode === "schema" && (
                <div className="flex flex-col gap-2.5">
                  <VaultVariableAutocomplete variables={variables} className="w-full">
                    <Textarea
                      placeholder={t(
                        "capabilities.webhookConfig.fieldsForm.fieldDescriptionPlaceholder",
                      )}
                      value={field.description ?? ""}
                      onChange={(e) => handleFieldChange(index, { description: e.target.value })}
                      rows={2}
                      className="w-full min-h-12"
                    />
                  </VaultVariableAutocomplete>
                  <div className="flex flex-row w-full items-center justify-between gap-2.5">
                    <Label
                      className="flex flex-row items-center gap-2.5"
                      htmlFor={`required-${index}`}
                    >
                      <Checkbox
                        id={`required-${index}`}
                        checked={field.required ?? false}
                        onCheckedChange={(checked) =>
                          handleFieldChange(index, { required: checked === true })
                        }
                      />
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-sm font-normal cursor-pointer underline decoration-dashed decoration-[0.5px] underline-offset-3 hover:cursor-pointer">
                            {t("capabilities.webhookConfig.fieldsForm.fieldRequired")}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {t("capabilities.webhookConfig.fieldsForm.fieldRequiredTooltip")}
                        </TooltipContent>
                      </Tooltip>
                    </Label>
                    {fields.length > minFields && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => handleRemoveField(index)}
                        className="shrink-0"
                        aria-label={t("capabilities.webhookConfig.fieldsForm.removeField")}
                      >
                        <Trash2 className="size-4" />
                        <span className="text-sm font-normal">
                          {t("capabilities.webhookConfig.fieldsForm.removeField")}
                        </span>
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
